import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { FaTimes, FaUserClock, FaPen, FaTrash } from "react-icons/fa";
import "./AvailabilitySettings.css";
import UserAvailabilitySettings from "./UserAvailabilitySettings";

const AvailabilitySettings = ({
  isOpen,
  onClose,
  onSave,
  initialStartDate,
  initialEndDate,
  sectionId,
  courseId,
}) => {
  const [startDate, setStartDate] = useState(
    initialStartDate ? new Date(initialStartDate) : null
  );
  const [endDate, setEndDate] = useState(
    initialEndDate ? new Date(initialEndDate) : null
  );
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [specialAccess, setSpecialAccess] = useState([]);
  const [editingUser, setEditingUser] = useState(null);

  const fetchSpecialAccess = async () => {
    try {
      const getUserAvailabilityFunction = httpsCallable(
        functions,
        "getUserAvailability"
      );
      const result = await getUserAvailabilityFunction({ courseId, sectionId });
      setSpecialAccess(result.data || []);
    } catch (error) {
      console.error("Error fetching special access:", error);
    }
  };

  useEffect(() => {
    if (sectionId) {
      fetchSpecialAccess();
    }
  }, [sectionId]);

  const handleSave = () => {
    onSave({ startDate, endDate });
    onClose();
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserSettings(true);
  };

  const handleRemoveUserAvailability = async (userId) => {
    try {
      const removeUserAvailabilityFunction = httpsCallable(
        functions,
        "removeUserAvailability"
      );
      await removeUserAvailabilityFunction({
        courseId,
        sectionId,
        userId,
      });

      // Update the local state to reflect the changes
      setSpecialAccess((prevSpecialAccess) =>
        prevSpecialAccess.filter((user) => user.userId !== userId)
      );

      fetchSpecialAccess(); // Refresh the list
    } catch (error) {
      console.error("Error removing user availability:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="availability-modal">
      <div className="availability-content">
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
        <h2>Section Availability Settings</h2>
        <div className="date-picker-container">
          <div className="date-field">
            <label>Available From:</label>
            <div className="date-input-container">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                showTimeSelect
                dateFormat="MMMM d, yyyy h:mm aa"
                placeholderText="Select start date and time"
                className="date-input"
              />
              {startDate && (
                <button
                  className="date-delete-button"
                  onClick={() => setStartDate(null)}
                  title="Remove start date"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
          <div className="date-field">
            <label>Available Until:</label>
            <div className="date-input-container">
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                showTimeSelect
                dateFormat="MMMM d, yyyy h:mm aa"
                placeholderText="Select end date and time"
                className="date-input"
                minDate={startDate}
              />
              {endDate && (
                <button
                  className="date-delete-button"
                  onClick={() => setEndDate(null)}
                  title="Remove end date"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
        </div>
        {specialAccess.length > 0 && (
          <div className="special-access-list">
            <h4>Users with Special Access:</h4>
            {specialAccess.map((user) => (
              <div key={user.userId} className="special-access-item">
                <div className="special-access-content">
                  <FaUserClock className="special-access-icon" />
                  <div className="special-access-details">
                    <div className="user-name">
                      {user.displayName || user.email}
                    </div>
                    <div className="access-time">
                      {new Date(user.startDate).toLocaleString([], {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                    <div className="access-time">
                      {new Date(user.endDate).toLocaleString([], {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                  </div>
                </div>
                <div className="special-access-actions">
                  <button
                    className="special-access-edit-button"
                    onClick={() => handleEditUser(user)}
                    title="Edit user special access"
                  >
                    <FaPen />
                  </button>
                  <button
                    className="special-access-delete-button"
                    onClick={() => handleRemoveUserAvailability(user.userId)}
                    title="Delete user special access"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="button-group">
          <button className="save-button" onClick={handleSave}>
            Save
          </button>
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="user-settings-button"
            onClick={() => setShowUserSettings(true)}
            title="User-specific availability settings"
          >
            <FaUserClock />
          </button>
        </div>
        {showUserSettings && (
          <UserAvailabilitySettings
            isOpen={showUserSettings}
            onClose={() => {
              setShowUserSettings(false);
              setEditingUser(null);
              fetchSpecialAccess();
            }}
            sectionId={sectionId}
            courseId={courseId}
            editingUser={editingUser}
          />
        )}
      </div>
    </div>
  );
};

export default AvailabilitySettings;
